import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { unitId, actionId } from '@/annotations'
import { Alert } from '@/components/Alert'
import { Button } from '@/components/Button'
import { Chip } from '@/components/Chip'
import { Note } from '@/components/Note'
import { Pager } from '@/components/Pager'
import { GridTable } from '@/components/GridTable'
import { useCursorPager } from '@/data/useCursorPager'
import { getApiClient, unwrap, ApiProblemError } from '@/data/apiClient'
import { UnexpectedRefusal, isUnexpectedRefusal } from '@/screens/auth-shared/Refusals'
import { useActiveOrgId, useActiveWorkspaceId } from '@/data/store'
import { formatJobTime } from '@/screens/shared/jobFormatting'
import type { components } from '@/api/generated/schema'
import type { Problem } from '@/data/apiClient'

type Repository = components['schemas']['Repository']
type RepositoryScope = components['schemas']['RepositoryScope']
type WorkspaceSummary = components['schemas']['WorkspaceSummary']

/**
 * `v-notprotected` — process/09-SCREEN-SPECS/v-notprotected.md. **A live operational screen, not a
 * report** (rule 1: no `reportGet`, no export, and its rows carry an action — W108) — it reads the
 * **live** repository list with `in_scope=false` (rule 2: this is one of the two surfaces §7a's
 * snapshot fence does not bind, alongside `v-setrepos`, because this is what is protected NOW, not
 * what a past snapshot holds). "Protect all" reads its own count from `RepositoryScope.exclusions`
 * — a full, unpaged array — rather than from this cursor-paged list, which the fence forbids a
 * total on (rule 25); protecting clears every exclusion in one `workspaceReplaceScope` call, the
 * same audited write `v-setrepos`'s per-row toggle already makes (rule 3).
 *
 * **`chip` added W166** — the same `excludedCount` the "Protect all" button already reads, shown
 * beside the title.
 */
export function VNotprotected() {
  const orgId = useActiveOrgId()!
  const workspaceId = useActiveWorkspaceId()!
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const pager = useCursorPager()

  const scope = useQuery({
    queryKey: ['workspace-scope', orgId, workspaceId],
    // Found live, 2026-09-25 (systemic audit after VHealth.tsx/VDrift.tsx's own fix) — see that
    // fix's own doc comment for the mount-time/org-switch race this guards against.
    enabled: Boolean(orgId && workspaceId),
    queryFn: async (): Promise<RepositoryScope> => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/scope', { params: { path: { org_id: orgId, workspace_id: workspaceId } } })
      if (error) throw new ApiProblemError(error as Problem)
      return data as unknown as RepositoryScope
    },
  })

  const repositories = useQuery({
    queryKey: ['not-protected-repositories', orgId, workspaceId, pager.cursor],
    enabled: Boolean(orgId && workspaceId),
    queryFn: async () => {
      const client = getApiClient()
      const { data, error } = await client.GET('/orgs/{org_id}/workspaces/{workspace_id}/repositories', {
        params: { path: { org_id: orgId, workspace_id: workspaceId }, query: { cursor: pager.cursor, in_scope: false } },
      })
      if (error) throw new ApiProblemError(error as Problem)
      return data
    },
  })

  const replaceScope = useMutation({
    mutationFn: async (exclusions: RepositoryScope['exclusions']) => {
      const client = getApiClient()
      const response = await client.PUT('/orgs/{org_id}/workspaces/{workspace_id}/scope', {
        params: { path: { org_id: orgId, workspace_id: workspaceId } },
        body: { exclusions },
      })
      return unwrap(response as { data?: RepositoryScope; error?: Problem })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['workspace-scope', orgId, workspaceId] })
      queryClient.invalidateQueries({ queryKey: ['not-protected-repositories', orgId, workspaceId] })
    },
  })

  const problem = repositories.error instanceof ApiProblemError ? repositories.error.problem : undefined
  const rows = (repositories.data?.items ?? []) as unknown as readonly Repository[]
  const excludedCount = scope.data?.exclusions.length ?? 0
  // Reads the already-fetched `['workspaces', orgId]` cache `ShellLayer` populates rather than a
  // new `workspaceGet` call — that operation's own contract (`x-screens`) does not name this screen.
  const workspaceName = queryClient.getQueryData<readonly WorkspaceSummary[]>(['workspaces', orgId])?.find((w) => w.workspace_id === workspaceId)?.name

  return (
    <div data-scr={unitId('v-notprotected')}>
      {excludedCount > 0 && (
        <div className="mb-6">
          <Alert variant="warn">
            <b>Automatic protection of new repositories is off{workspaceName ? ' for ' : ''}</b>
            {workspaceName && <span className="font-mono">{workspaceName}</span>}
            <b>.</b> Repositories created since that setting was changed are listed here and are not being
            backed up.
          </Alert>
        </div>
      )}
      <div data-cmp="panel-header" className="flex items-center gap-5 mb-6">
        <h1 className="m-0 text-section font-semibold flex-1">Not protected</h1>
        {/* `Prototype_v2.html`'s own count beside this heading is `span.chip.solid-warn` — found
            rendering `plain` (grey) instead by `semantic-diff.mjs`'s colour comparison. */}
        {excludedCount > 0 && <Chip variant="warning">{excludedCount}</Chip>}
        <div data-cmp="grid-tools">
          {excludedCount > 0 && (
            <>
              <Button data-act={actionId('nav.v-setrepos')} ghost onClick={() => navigate('/setrepos')}>
                Change the setting
              </Button>
              <Button data-act={actionId('scope.replace')} disabled={replaceScope.isPending} onClick={() => replaceScope.mutate([])}>
                Protect all {excludedCount}
              </Button>
            </>
          )}
        </div>
      </div>

      {problem && (isUnexpectedRefusal(problem.refusal_kind) ? <UnexpectedRefusal problem={problem} /> : <Note>{problem.title}</Note>)}

      {!problem && rows.length === 0 && !repositories.isLoading && <Note>Every repository is protected.</Note>}

      {rows.length > 0 && (
        <div data-cmp="panel" className="bg-panel border border-bd rounded-2">
          <GridTable
            columns={[
              { key: 'repository', header: 'Repository', render: (r) => r.name },
              { key: 'project', header: 'Project', render: (r) => <span className="text-muted">{r.project_key ?? '—'}</span> },
              { key: 'created', header: 'Created', render: (r) => (r.created_at ? formatJobTime(r.created_at) : '—') },
              { key: 'size', header: 'Size', render: (r) => r.size_bytes?.display ?? '—' },
              {
                key: 'action',
                header: '',
                render: (r) => (
                  <Button
                    data-act={actionId('scope.replace')}
                    ghost
                    disabled={replaceScope.isPending}
                    onClick={() => replaceScope.mutate((scope.data?.exclusions ?? []).filter((e) => e.repository_id !== r.repository_id))}
                  >
                    Protect
                  </Button>
                ),
              },
            ]}
            rows={rows}
            getRowKey={(r) => r.repository_id}
          />
          <div className="p-5 border-t border-bd">
            <Note>
              This list and its count badge stay visible while the setting is off. Turning automatic protection back on protects
              these {excludedCount} and empties the list. Either change is written to the audit log.
            </Note>
          </div>
          <div data-cmp="grid-footer" className="border-t border-bd flex items-center justify-between p-5">
            <Note>Paged by cursor only — no total is served for this list.</Note>
            <Pager hasPrev={pager.hasPrev} hasNext={Boolean(repositories.data?.next_cursor)} loading={repositories.isFetching} onPrev={pager.goPrev} onNext={() => repositories.data?.next_cursor && pager.goNext(repositories.data.next_cursor)} />
          </div>
        </div>
      )}
    </div>
  )
}
